import type { TableColumn } from '@interstate/components/Table';
import isEqual from 'lodash.isequal';
import type { LenderGuideLineItem } from '@makemydeal/dr-dash-store/types/guidelineAlertsTypes';

type DataIndex = string | number | readonly (string | number)[];

/**
 * Remove a column (by dataIndex) from the columns array defined for Interstate Table without mutating the original array. This will work for nested data indexes with array values.
 * @param cols All columns before modification
 * @param removeFields List of data indexes to remove. (this might an array of strings or an array of string arrays)
 * @returns A new array with filtered columns.
 */
export const removeColumns = (cols: TableColumn[], removeFields: DataIndex[]) => {
    const result = [...cols];
    removeFields.forEach((dataIndex) => {
        const ix = indexOfDataIndex(result, dataIndex);
        if (ix < 0) return;
        result.splice(ix, 1);
    });
    return result;
};

/**
 * Return the index of a column matching the dataIndex provided
 * @param cols Columns to search
 * @param lookForIndex dataIndex to check for
 * @returns index of the matching column or -1
 */
export const indexOfDataIndex = (cols: TableColumn[], lookForIndex: DataIndex): number => {
    const found = cols.find((c) => isEqual(c.dataIndex, lookForIndex)); // could be string or string[]; need to compare values
    if (!found) return -1;
    const ix = cols.indexOf(found);
    return ix;
};

export const filterUniqueMessages = (guideLineAlerts: LenderGuideLineItem[]): LenderGuideLineItem[] => {
    const seenMessages = new Set<string>();
    return guideLineAlerts.filter((messageItem) => {
        const uniqueKey = `${messageItem.lenderCode}_${messageItem.message}`;
        if (seenMessages.has(uniqueKey)) {
            return false;
        } else {
            seenMessages.add(uniqueKey);
            return true;
        }
    });
};

/**
 * Return columns array in rearranged order based on dataIndex of columns
 * @param orderArray
 * @param columns
 * @returns
 */
export const reorderColumnsByDataIndex = (cols: TableColumn[], orderArray: DataIndex[]) => {
    const result = [...cols];
    const columnMap = new Map<string, TableColumn>(result.map((col) => [JSON.stringify(col.dataIndex), col]));

    return orderArray
        .map((dataIndex) => columnMap.get(JSON.stringify(dataIndex)))
        .filter((col): col is TableColumn => Boolean(col));
};

/**
 * Pin the leading columns to the left edge, by position, without mutating the input.
 * @param cols Columns in their final render order
 * @param count How many leading columns to pin
 * @returns A new array whose first `count` columns are pinned left
 */
export const withFrozenColumns = (cols: TableColumn[], count: number): TableColumn[] =>
    cols.map((col, ix) => (ix < count ? { ...col, fixed: 'left' } : col));
